import { type FunctionComponent } from "react";
import { CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { Badge, Button, Popconfirm, Popover, Space, Tag, Typography } from "antd";
import { BadgeAlertIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  FilterOperator,
  type ScheduledPopUpMessagesAutomationFromAutomation,
} from "@fitness-app/data-models/entities/AutomationTemplate";
import { MediaContentType } from "@fitness-app/data-models/entities/Campaign";
import { ProgramAutomationTaskStatus } from "@fitness-app/data-models/entities/ProgramAutomation";

import { formatSentTime, formatSentTimeLong } from "~/modules/Automation/utils/formatSentTime";

interface OwnProps {
  item: ScheduledPopUpMessagesAutomationFromAutomation;
  onRemove: () => void;
  onEdit?: () => void;
  status?: ProgramAutomationTaskStatus;
  sentDate?: string;
}

type Props = OwnProps;

const statusMapper = {
  [ProgramAutomationTaskStatus.Upcoming]: "processing",
  [ProgramAutomationTaskStatus.Done]: "success",
  [ProgramAutomationTaskStatus.Rejected]: "error",
  [ProgramAutomationTaskStatus.Canceled]: "warning",
  // Nie było czego zrobić i to normalne (np. brak bieżącej edycji wyzwania) —
  // szary, żeby nie mylił się z błędem ani z odfiltrowaniem klientki.
  [ProgramAutomationTaskStatus.Skipped]: "default",
} as const;

const contentTypeLabels = {
  [MediaContentType.None]: "Brak mediów",
  [MediaContentType.Image]: "Obraz",
  [MediaContentType.Video]: "Wideo",
  [MediaContentType.Youtube]: "YouTube",
};

const PopUpAutomationItem: FunctionComponent<Props> = ({ item, onRemove, status, onEdit, sentDate }) => {
  const { t } = useTranslation(["automation", "campaigns"]);

  const handleEdit: React.MouseEventHandler<HTMLElement> = (event) => {
    event.stopPropagation();
    onEdit?.();
  };

  return (
    <Popover
      title={t("campaigns:popup.title")}
      content={
        <Space direction="vertical" className="max-w-[400px]">
          {status && (
            <Space>
              <Typography.Text type="secondary">{t("automation:events.status")}</Typography.Text>
              <Badge
                status={statusMapper[status] || "default"}
                text={t(`automation:eventsStatus.${status || "upcoming"}`)}
              />
            </Space>
          )}
          <Space>
            <Typography.Text type="secondary">{t("campaigns:popup.form.name")}</Typography.Text>
            <Typography.Text>{item.name}</Typography.Text>
          </Space>
          <Space>
            <Typography.Text type="secondary">{t("automation:events.sentTime")}</Typography.Text>
            <Typography.Text>{formatSentTimeLong(item.sentTime)}</Typography.Text>
          </Space>
          <Space>
            <Typography.Text type="secondary">{t("campaigns:popup.form.contentType")}</Typography.Text>
            <Typography.Text>{contentTypeLabels[item.data.contentType]}</Typography.Text>
          </Space>
          <Space>
            <Typography.Text type="secondary">{t("campaigns:popup.form.visualType")}:</Typography.Text>
            <Typography.Text>{item.data.visualType === "modal" ? "Modal" : "Bottom sheet"}</Typography.Text>
          </Space>
          <Space>
            <Typography.Text type="secondary">{t("automation:events.messagesAuthor")}:</Typography.Text>
            <Typography.Text>{item.authorName}</Typography.Text>
          </Space>
          {item.filters && item.filters.length > 0 ? (
            <Space direction="vertical" size={4}>
              <div className="mb-2 flex items-center gap-2">
                <Typography.Text type="secondary">Filtry:</Typography.Text>
                <Typography.Text keyboard>{item.filterOperator === FilterOperator.AND ? "AND" : "OR"}</Typography.Text>
              </div>

              <div className="flex flex-wrap gap-2">
                {item.filters.map((filter, index) => (
                  <Tag key={index} color="blue">
                    {t(`dashboard:automatedMessageForm.${filter.type}`)}:{" "}
                    {t(`dashboard:automatedMessageForm.${filter.condition}`)}:{" "}
                    {filter.type === "calendarEvents"
                      ? t(`dashboard:automatedMessageForm.${filter.range}`)
                      : filter.tags.join(", ")}
                  </Tag>
                ))}
              </div>
            </Space>
          ) : null}
          <Space>
            {onEdit && status !== ProgramAutomationTaskStatus.Done && (
              <Button icon={<EditOutlined style={{ pointerEvents: "none" }} />} type="link" onClick={handleEdit} />
            )}
            {onRemove && status !== ProgramAutomationTaskStatus.Done && (
              <Popconfirm
                title="Czy na pewno chcesz usunąć ten popup?"
                okText={t("common:switch.yes")}
                cancelText={t("common:button.cancel")}
                placement="top"
                onConfirm={onRemove}
              >
                <Button danger icon={<DeleteOutlined style={{ pointerEvents: "none" }} />} type="text" />
              </Popconfirm>
            )}
          </Space>
        </Space>
      }
    >
      <BadgeAlertIcon size={16} className="mr-2 align-text-top" />
      <Typography.Text>{formatSentTime(item.sentTime, sentDate)}</Typography.Text>

      {status === ProgramAutomationTaskStatus.Done && <CheckOutlined className="ml-2 text-green-500" />}
      {status === ProgramAutomationTaskStatus.Canceled && <CloseOutlined className="ml-2 text-orange-500" />}
    </Popover>
  );
};

export default PopUpAutomationItem;
